import React, { memo, useMemo, useLayoutEffect, useRef } from 'react';
import useTheme from '../hooks/useTheme';
import { Themes } from '../types';
import { createTheme, CSS, styled } from './stitches.config';
// Styles
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import calendarStylesLight from './calendar-styles-light.css';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import styles from './slick-styles.css';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import calendarStylesDark from './calendar-styles-dark.css';
import { SMPLoader } from '../ui';
import { MAX_WIDGET_WIDTH } from '../constants/generic';

export type ThemeProviderProps = {
  children: React.ReactNode;
  css?: CSS;
  dataTheme?: Themes | null;
  themes?: { dark?: unknown; light?: unknown; client?: unknown };
  shadow?: ShadowRoot | null;
  slickStyles?: boolean;
  datePickerStyles?: string;
  isLoading: boolean;
  refForShadow?: React.RefObject<HTMLDivElement>;
  removeMaxWidth?: boolean;
  loaderMinHeight?: string;
  tailwindStyles?: string;
};

const addNewShadowStyles = (shadow: ShadowRoot | null | undefined, styles: string) => {
  if (!shadow) return '';

  // Remove existing styles
  Array.from(shadow.querySelectorAll('style'))
    .filter((item) => item.id !== 'tailwind-styles')
    .forEach((item) => item.remove());

  // Add new styles
  const styleNode = document.createElement('style');
  styleNode.textContent = styles;
  shadow.appendChild(styleNode);

  return styleNode.textContent;
};

function ThemeProvider({
  themes,
  dataTheme,
  children,
  css,
  shadow,
  slickStyles,
  datePickerStyles,
  isLoading,
  refForShadow,
  removeMaxWidth,
  loaderMinHeight = '300px',
  tailwindStyles,
}: ThemeProviderProps) {
  const themeName = dataTheme ? dataTheme : 'light';
  const themeApplied = useTheme(themeName, themes);
  const currentTheme = useMemo(() => createTheme(themeApplied.themeName, themeApplied.themeStyles), [themeApplied]);
  const previousStylesRef = useRef('');

  useLayoutEffect(() => {
    if (!shadow) return;

    let checkCount = 0;
    const maxChecks = 23;

    if (checkCount === 0) {
      if (tailwindStyles && !shadow.querySelector('style#tailwind-styles')) {
        const tailwindStylesNode = document.createElement('style');
        tailwindStylesNode.id = 'tailwind-styles';
        tailwindStylesNode.textContent = tailwindStyles;
        shadow.appendChild(tailwindStylesNode);
      }

      // Load the initial styles to prevent big pop-in elements
      const initialStyles = themeApplied.getCssText();
      const stylesAppended = addNewShadowStyles(shadow, initialStyles);
      previousStylesRef.current = stylesAppended;
      checkCount++;
    }

    const checkAndUpdateStyles = () => {
      const newStyles = themeApplied.getCssText();

      if (newStyles !== previousStylesRef.current || checkCount < maxChecks) {
        const stylesAppended = addNewShadowStyles(shadow, newStyles);

        // Add date picker styles if needed
        if (datePickerStyles) {
          const datepickerStylesNode = document.createElement('style');
          const calendarStylesNode = document.createElement('style');

          datepickerStylesNode.textContent = datePickerStyles;
          calendarStylesNode.textContent = dataTheme === 'light' ? calendarStylesLight : calendarStylesDark;

          shadow.appendChild(datepickerStylesNode);
          shadow.appendChild(calendarStylesNode);
        }

        // Add slick styles if needed
        if (slickStyles) {
          const slickStylesNode = document.createElement('style');
          slickStylesNode.textContent = styles;
          shadow.appendChild(slickStylesNode);
        }

        previousStylesRef.current = stylesAppended;
      }

      checkCount++;
      if (checkCount >= maxChecks) {
        clearInterval(intervalId);
      }
    };

    // Start checking every 200ms
    const intervalId = setInterval(checkAndUpdateStyles, 200);

    return () => {
      clearTimeout(intervalId);
    };
  }, [shadow, currentTheme, dataTheme, themeApplied, datePickerStyles, slickStyles]);

  const ThemeContainer = useMemo(() => styled('div'), []);

  return (
    <ThemeContainer
      className={themeName}
      css={{
        maxWidth: removeMaxWidth ? undefined : MAX_WIDGET_WIDTH + 'px',
        ...css,
        backgroundColor: isLoading ? 'transparent' : css?.backgroundColor,
      }}
      ref={refForShadow}
    >
      {isLoading ? <SMPLoader size={3} thickness={4} color='spinner' loaderMinHeight={loaderMinHeight} /> : children}
    </ThemeContainer>
  );
}

export default memo(ThemeProvider);
